import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Hash, Mail, Calendar } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, booking: null });

  useEffect(() => {
    if (!sessionId) { setState({ loading: false, paid: false, booking: null }); return; }
    let tries = 0;
    const tick = async () => {
      tries++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ loading: false, paid: true, booking: data.booking });
          return;
        }
      } catch {}
      if (tries < 12) setTimeout(tick, 1500);
      else setState((s) => ({ ...s, loading: false }));
    };
    tick();
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      {state.loading ? (
        <div>
          <div className="animate-pulse text-[#0B1F3A]/60">Confirming your payment…</div>
        </div>
      ) : state.paid && state.booking ? (
        <>
          <CheckCircle2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <div className="eyebrow mb-2" style={{ color: "#0B1F3A" }}>Booking confirmed</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tighter mb-3">Enjoy the drive.</h1>
          <p className="text-[#0B1F3A]/70 mb-8">Your reservation is confirmed. A copy of your details is below.</p>
          <div className="bg-white rounded-2xl border border-black/5 p-8 text-left space-y-3">
            <Row icon={Hash} label="Reference" value={state.booking.reference} />
            <Row icon={Mail} label="Email" value={state.booking.customer_email} />
            <Row icon={Calendar} label="Pickup" value={new Date(state.booking.pickup_datetime).toLocaleString()} />
            <Row icon={Calendar} label="Return" value={new Date(state.booking.return_datetime).toLocaleString()} />
            <div className="border-t border-black/5 pt-3 flex justify-between text-lg font-display font-bold">
              <span>Total paid</span><span className="text-[#D4AF37]" data-testid="success-total">${state.booking.total_amount.toFixed(2)}</span>
            </div>
          </div>
          <Link to="/my-bookings" className="btn-gold mt-8 inline-flex" data-testid="success-view-bookings">View my bookings</Link>
        </>
      ) : (
        <>
          <h1 className="font-display font-extrabold text-4xl tracking-tighter mb-4">We couldn't confirm your payment</h1>
          <p className="text-[#0B1F3A]/70 mb-8">If you were charged, please contact our concierge — we'll sort it out immediately.</p>
          <Link to="/fleet" className="btn-gold inline-flex">Return to fleet</Link>
        </>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-[#D4AF37]" />
      <div className="text-xs uppercase tracking-widest text-[#0B1F3A]/50 w-24">{label}</div>
      <div className="text-[#0B1F3A] font-medium">{value}</div>
    </div>
  );
}
